import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { RootState } from 'handlers';
import { RoutePath } from 'enums/Routes';
import { useNavigate } from 'hooks/useNavigate';
import { getPaymentAccount } from 'thunks';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import { getApplicationData } from 'selectors/getApplicationData';
import { getPaymentAccountData } from 'selectors/paymentAccount';
import Button from 'components/Button';
import FormContainer from 'components/LoanForm/FormContainer';
import Loader from 'components/Loader';
import { ErrorType } from 'components/Error/ErrorType';
import FormNavigation from 'components/FormNavigation';
import CardSequenceWithIcons, { CardSequenceRightIcon } from 'components/Common/CardSequenceWithIcons';
import { getHighestTierCredential } from 'utils/credentialUtils';
import { RepaymentMethodResult } from 'enums/FlowNextResults';
import { FlowComponentType } from 'routes/types';
import { formatMonetaryAmount } from 'utils/formatMonetaryAmount';

import { ReactComponent as PaycheckIcon } from 'images/repayment-items/paycheck-icon.svg';
import { ReactComponent as MoneyIcon } from 'images/repayment-items/money-icon.svg';
import { ReactComponent as MoneyBagIcon } from 'images/repayment-items/money-bag-icon.svg';
import { ReactComponent as WalletIcon } from 'images/repayment-items/wallet-icon.svg';

import styles from './RepaymentMethod.module.scss';

const RepaymentMethod = ({ navigationInfo, handleNext }: FlowComponentType) => {
  const navigate = useNavigate();
  const dispatchWithUnwrap = useDispatchWithUnwrap();

  const bottomRef = useRef<HTMLDivElement>(null);
  const [employerName, setEmployerName] = useState<string>('');
  const [logo, setLogo] = useState<string | null>(null);

  const { application } = useSelector(getApplicationData);
  const { status, isInvalid } = useSelector(getPaymentAccountData);

  const { isLoading: isFetchingEmployerData, error: errorFetchingEmployerData, employerData } = useSelector(
    (state: RootState) => state.employers,
  ).getEmployerData;

  const { isLoading } = status.createPaymentAccount;
  const { hasStarted: getPaymentAccountStarted, isLoading: getPaymentAccountLoading } = status.getPaymentAccount;

  const applicationId = application?.id;

  const employer1 = application?.employment.employer1;

  const manualUploadedLogo = employer1?.logo || null;
  const isFullDepositSwitchEnabled = application?.fullDepositSwitch;

  useEffect(() => {
    if (application?.completedRepaymentAccountStep) {
      handleNext(RepaymentMethodResult.CompletedRepaymentAccountStep);
    }
  }, [application?.completedRepaymentAccountStep]);

  // Preload logo image to prevent loading delay
  useEffect(() => {
    if (logo) {
      const img = new Image();
      img.src = logo;
    }
  }, [logo]);

  useEffect(() => {
    if (errorFetchingEmployerData || !employerData) {
      setEmployerName(employer1?.employerName!);
    } else if (employerData) {
      setEmployerName(employerData.name);
      setLogo(employerData.logo_url);
    }
  }, [isFetchingEmployerData, errorFetchingEmployerData, employerData]);

  useEffect(() => {
    if (!logo || !employerName) {
      getLogo();
    }
    fetchPaymentAccount();
  }, []);

  useEffect(() => {
    if (isInvalid) {
      navigate(RoutePath.Error, { state: { type: ErrorType.Default } });
    }
  }, [isInvalid]);

  const getLogo = async () => {
    if (!logo && manualUploadedLogo) {
      setLogo(manualUploadedLogo);
    }
  };

  const fetchPaymentAccount = async () => {
    if (applicationId && !getPaymentAccountStarted) {
      await dispatchWithUnwrap(getPaymentAccount({ applicationId }));
    }
  };

  const handleContinue = async () => {
    analytics.track('Open DDA Screen');
    handleNext();
  };

  const renderLogo = () => {
    if (logo) {
      return <img src={logo} alt="logo" className={styles.employerLogo} />;
    }

    return null;
  };

  return (
    <>
      <FormNavigation {...navigationInfo} />
      <FormContainer
        className={styles.container}
        title="Set up direct deposit from your paycheck"
        subtitle="How it works:"
      >
        <div className={styles.cardSequenceContainer}>
          {isFetchingEmployerData || getPaymentAccountLoading ? (
            <div className={styles.loaderContainer}>
              <Loader color="#795af7" />
            </div>
          ) : (
            <div className={styles.repaymentStep}>
              <CardSequenceWithIcons
                icon={renderLogo() || <PaycheckIcon />}
                title={
                  !isFullDepositSwitchEnabled ? formatMonetaryAmount(application?.amountPerPaycheck, true) : undefined
                }
                description={isFullDepositSwitchEnabled ? 'Your paycheck' : 'from your paycheck'}
                rightIcon={CardSequenceRightIcon.Arrow}
              />

              <CardSequenceWithIcons
                icon={<MoneyIcon />}
                description={
                  isFullDepositSwitchEnabled
                    ? 'Plannery direct deposit account'
                    : 'To direct deposit account for loan payments*'
                }
                rightIcon={CardSequenceRightIcon.Arrow}
              />

              <CardSequenceWithIcons
                icon={<MoneyBagIcon />}
                title={formatMonetaryAmount(application?.amountPerPaycheck, true)}
                description="to your loan payment"
                rightIcon={CardSequenceRightIcon.Checked}
              />

              <CardSequenceWithIcons
                withBorder={false}
                icon={<WalletIcon />}
                description="The rest of your paycheck goes to your regular account."
              />
            </div>
          )}
        </div>

        {application?.borrowerCredentials && (
          <div className={styles.blueCard}>
            <p>
              {getHighestTierCredential(application.borrowerCredentials)}s like you get lower than average interest
              rates with direct deposit from paycheck.**
            </p>
          </div>
        )}

        <Button isLoading={isLoading} onClick={handleContinue}>
          Next
        </Button>
      </FormContainer>
      <div ref={bottomRef} />
    </>
  );
};

export default RepaymentMethod;
